import { FileText, ScanSearch } from 'lucide-react';
import type { ReactNode } from 'react';

import type { BatchStatus } from '~/features/catalog-operations/types/catalog-operations.types';

/** Batch source tags whose flow chip diverges from the default platform logos. */
const FEED_AUDIT_SOURCE = 'FEED_AUDIT';
const TRIAL_SOURCE = 'TRIAL';
const EXPERIMENT_SOURCE = 'EXPERIMENT';

/** Whether a batch `source` is an experiment run — its enrichment workflow lives
 *  on the experiment timeline, so the "open workflow" action routes there. */
export function isExperimentSource(source: string): boolean {
  return source === EXPERIMENT_SOURCE;
}

/** Whether a batch `source` is a Feed Audit run — these audit rather than
 *  deliver, so their destination shows the scan-search icon, not a platform. */
export function isFeedAuditSource(source: string): boolean {
  return source === FEED_AUDIT_SOURCE;
}

/** Status the batch chip shows. Feed Audit runs audit rather than deliver, so a
 *  `delivered` run reads as Completed. Display-only: filters and delivery logic
 *  keep the raw `status`. */
export function resolveBatchChipStatus(status: BatchStatus, source: string): BatchStatus {
  return isFeedAuditSource(source) && status === 'delivered' ? 'completed' : status;
}

/** Whether a batch `source` is a trial run — these read from and write to a
 *  sample file, so both ends of the flow show the file-text icon. */
export function isTrialSource(source: string): boolean {
  return source === TRIAL_SOURCE;
}

/** Source/destination glyph overrides for the {@link SourceDestinationChip},
 *  keyed off the raw batch trigger source. Feed Audit swaps the destination
 *  for a scan-search icon; Trial swaps both ends for file-text; every other
 *  source falls back to the platform logos (empty object). */
export function batchFlowIcons(source: string): {
  sourceIcon?: ReactNode;
  destinationIcon?: ReactNode;
} {
  if (isFeedAuditSource(source)) {
    return {
      destinationIcon: (
        <ScanSearch className="text-accent-main h-5 w-5 shrink-0" aria-label="Feed Audit" />
      ),
    };
  }
  if (isTrialSource(source)) {
    return {
      sourceIcon: (
        <FileText className="text-muted-foreground h-5 w-5 shrink-0" aria-label="Trial" />
      ),
      destinationIcon: (
        <FileText className="text-muted-foreground h-5 w-5 shrink-0" aria-label="Trial" />
      ),
    };
  }
  return {};
}
